import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

const CreateScheme = ({ user }) => {
  const navigate = useNavigate();
  const { schemeId } = useParams();
  const isEdit = !!schemeId;
  
  const [formData, setFormData] = useState({
    name: '',
    status: 'Active',
    payment_mode: 'AMOUNT_PER_PERIOD',
    periods_customer_pays: 11,
    periods_bonus_company: 1,
    frequency: 'Monthly',
    notes: ''
  });
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadScheme();
    }
  }, [schemeId]);

  const loadScheme = async () => {
    try {
      const res = await axiosInstance.get(`/kitty/schemes/${schemeId}`);
      setFormData(res.data);
    } catch (err) {
      toast.error('Error loading scheme');
      navigate('/kitty/schemes');
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Please enter scheme name');
      return;
    }
    
    if (formData.periods_customer_pays < 1 || formData.periods_bonus_company < 0) {
      toast.error('Invalid period values');
      return;
    }

    setLoading(true);
    try {
      if (isEdit) {
        await axiosInstance.put(`/kitty/schemes/${schemeId}`, formData);
        toast.success('Scheme updated successfully');
      } else {
        await axiosInstance.post('/kitty/schemes', formData);
        toast.success('Scheme created successfully');
      }
      navigate('/kitty/schemes');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving scheme');
    } finally {
      setLoading(false);
    }
  };

  const effectiveRole = user.effective_role || user.role;
  if (effectiveRole !== 'Admin' && effectiveRole !== 'Manager') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4">
        <div className="max-w-2xl mx-auto mt-20 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only Admin/Manager can create schemes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-32">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b-2 border-amber-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/kitty/schemes')} className="p-2 hover:bg-amber-100 rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold text-gray-800">{isEdit ? 'Edit' : 'Create'} Scheme</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-4 max-w-2xl mx-auto">
        {/* Basic Info */}
        <div className="premium-card border-2 border-amber-200 p-4">
          <h3 className="text-sm font-bold text-gray-800 mb-3">📋 Basic Information</h3>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Scheme Name *</label>
              <input
                type="text"
                className="input-field w-full"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., 11+1 Gold Saver, 11+1 Grams Scheme"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
                <select
                  className="input-field w-full"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  <option value="Active">✅ Active</option>
                  <option value="Inactive">⏸️ Inactive</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Frequency</label>
                <select
                  className="input-field w-full"
                  value={formData.frequency}
                  onChange={(e) => handleChange('frequency', e.target.value)}
                >
                  <option value="Monthly">📅 Monthly</option>
                  <option value="Weekly">🗓️ Weekly</option>
                  <option value="Quarterly">📆 Quarterly</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Structure - SIMPLIFIED */}
        <div className="premium-card border-2 border-green-200 p-4 bg-green-50">
          <h3 className="text-sm font-bold text-gray-800 mb-3">💰 Payment Structure</h3>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Payment Type *</label>
              <select
                className="input-field w-full"
                value={formData.payment_mode}
                onChange={(e) => handleChange('payment_mode', e.target.value)}
              >
                <option value="AMOUNT_PER_PERIOD">💵 Amount Per Period (e.g., ₹5,000/month)</option>
                <option value="GRAMS_PER_PERIOD">⚖️ Grams Per Period (e.g., 5g/month)</option>
              </select>
              <p className="text-xs text-gray-600 mt-1">
                {formData.payment_mode === 'AMOUNT_PER_PERIOD' && 'Customer pays fixed amount each period'}
                {formData.payment_mode === 'GRAMS_PER_PERIOD' && 'Customer buys fixed grams each period (amount varies with rate)'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Customer Pays *</label>
                <input
                  type="number"
                  className="input-field w-full"
                  value={formData.periods_customer_pays}
                  onChange={(e) => handleChange('periods_customer_pays', parseInt(e.target.value))}
                  min="1"
                  placeholder="11"
                />
                <p className="text-xs text-gray-500 mt-0.5">Number of periods customer pays</p>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Store Bonus *</label>
                <input
                  type="number"
                  className="input-field w-full"
                  value={formData.periods_bonus_company}
                  onChange={(e) => handleChange('periods_bonus_company', parseInt(e.target.value))}
                  min="0"
                  placeholder="1"
                />
                <p className="text-xs text-gray-500 mt-0.5">Free bonus periods from store</p>
              </div>
            </div>

            {/* Visual Preview */}
            <div className="bg-white p-3 rounded-lg border-2 border-green-300">
              <p className="font-semibold text-gray-700 text-sm">📊 Scheme Preview:</p>
              <p className="text-gray-800 mt-1 text-lg font-bold">
                {formData.periods_customer_pays} <span className="text-green-600">+ {formData.periods_bonus_company}</span> = {formData.periods_customer_pays + formData.periods_bonus_company} Total Periods
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Customer pays for {formData.periods_customer_pays} periods, gets {formData.periods_bonus_company} period(s) FREE!
              </p>
            </div>
          </div>
        </div>

        {/* Optional Settings - SIMPLIFIED */}
        <div className="premium-card border-2 border-blue-200 p-4">
          <h3 className="text-sm font-bold text-gray-800 mb-3">⚙️ Optional Settings</h3>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Notes (Internal)</label>
              <textarea
                className="input-field w-full"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Add any internal notes about this scheme..."
                rows="2"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-amber-200 p-4 shadow-lg">
          <div className="flex gap-3 max-w-2xl mx-auto">
            <button
              type="button"
              onClick={() => navigate('/kitty/schemes')}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create') + ' Scheme'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateScheme;